import React, {useEffect, useRef} from 'react';
import {useHistory, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {
    createPayerData,
    mapDepositDetails,
    mapPaymentDetails
} from "./PaymentFunctions";
import {getAndClearSessionStorage} from "../../utils";
import {getPaymentResponse} from "../../requests/TokenRequests";
import {
    resolveCurrencyKey,
    useCurrencyDecimalLookup
} from "../../selectors/ConfigSelectors";
import {useInvoiceSearchQuery} from "./InvoiceDataManager";
import {getInvoices, makeDeposit} from "../../actions/InvoiceActions";
import {DepositRequest} from "../../interfaces/DepositRequest";
import {CACHED_CVV} from "./Payment";
import { RootState } from "../../reducers/RootReducer";
import {
    consumeThreeDSFlowContext,
    handleThreeDSContextError,
    resolveRestoredPaymentOption,
    rehydrateThreeDSAccountContext,
    validateThreeDSFlowContext
} from "../../threeDSFlowContext";
import { AccountType } from "../../interfaces/Accounts";

export function ProcessingDepositPage(props: {}) {
    const location = useLocation();
    const history = useHistory();
    const search = new URLSearchParams(location.search);
    const accessToken = search.get("id");
    const dispatch = useDispatch();
    const currentUser = useSelector<any, any>(state => state.default.currentUser);
    const decimalLookup = useCurrencyDecimalLookup();
    const invoiceSearchQuery = useInvoiceSearchQuery();
    const primaryAccounts = useSelector<RootState, any>(state => state.user_options.primaryAccounts);
    const secondaryAccounts = useSelector<RootState, any>(state => state.user_options.secondaryAccounts);
    const accountType = useSelector<RootState, any>(state => state.user_options.accountType);
    const companyCodes = useSelector<RootState, any>(state => state.app.config?.sap?.data?.company_codes ?? []);
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (!accessToken) {
            history.replace("/");
            return;
        }
        if (hasProcessed.current) {
            return;
        }
        if (Object.keys(primaryAccounts ?? {}).length === 0 || !currentUser) {
            return;
        }

        (async () => {
            hasProcessed.current = true;
            const context = consumeThreeDSFlowContext();
            if (!validateThreeDSFlowContext(context, "deposit-payment")) {
                handleThreeDSContextError(history, "/deposits", "Your 3DS session expired. Please try the deposit again.");
                return;
            }

            try {
                const cvv = getAndClearSessionStorage(CACHED_CVV);
                let cached_deposit = sessionStorage.getItem("DEPOSIT_CACHE");
                if (!cached_deposit) {
                    throw new Error("We couldn't restore the pending deposit details.");
                }

                const resolvedAccounts = await rehydrateThreeDSAccountContext(
                    dispatch,
                    context,
                    primaryAccounts,
                    secondaryAccounts,
                    currentUser,
                    accountType,
                );
                let deposit_form = JSON.parse(cached_deposit);
                const restoredCustomerNumber = accountType === AccountType.SoldTo && resolvedAccounts.secondaryAccount
                    ? resolvedAccounts.secondaryAccount.PrimaryAcct
                    : resolvedAccounts.primaryAccount.PrimaryAcct;
                const restoredPaymentOption = resolveRestoredPaymentOption(
                    context,
                    resolvedAccounts.payerDetails,
                    restoredCustomerNumber,
                );
                const restoredPayerAccount = accountType === AccountType.SoldTo && resolvedAccounts.secondaryAccount
                    ? resolvedAccounts.secondaryAccount
                    : resolvedAccounts.primaryAccount;
                const restoredCurrencyKey = resolveCurrencyKey(restoredPayerAccount, companyCodes);
                let { cardinal } = await getPaymentResponse(accessToken);
                let depositDetail = mapDepositDetails(deposit_form, restoredCurrencyKey);
                let paymentDetails = mapPaymentDetails(
                    restoredPaymentOption,
                    cvv,
                    resolvedAccounts.payerDetails?.address_data,
                    cardinal
                );
                let payerData = createPayerData(restoredCustomerNumber, resolvedAccounts.primaryAccount.CompanyCode);
                let depositRequest: DepositRequest = {
                    deposit_detail: depositDetail,
                    payer_data: payerData,
                    payment_detail: paymentDetails,
                };
                let success: any = await dispatch(makeDeposit(depositRequest));
                sessionStorage.removeItem("DEPOSIT_CACHE");
                history.replace('/deposits/payment-status');
                if (success) {
                    dispatch(getInvoices(invoiceSearchQuery, decimalLookup));
                }
            } catch (error: any) {
                hasProcessed.current = false;
                sessionStorage.removeItem("DEPOSIT_CACHE");
                handleThreeDSContextError(history, "/deposits", error?.message ?? "We couldn't restore the original account for this 3DS flow.");
            }
        })();
    }, [accessToken, accountType, companyCodes, currentUser, decimalLookup, dispatch, history, invoiceSearchQuery, primaryAccounts, secondaryAccounts]);

    return <div/>;
}
